import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { CLASS_LABEL, type ClassRow, type DCAProgress } from '../../services/investment-plan-service';
import { dcaStatusLabel } from '../../utils/investment-plan';
import { baseMoney } from '../../stores/currency-store';

const STATUS_COLOR: Record<ClassRow['status'], string> = {
  within: 'var(--inflow-text)',
  over: 'var(--warn-text)',
  under: 'var(--warn-text)',
  no_target: 'var(--text-secondary)',
};

/**
 * Allocation against target, one bar per class (issue #31). The bar is the
 * actual share; the tick is the target. Drift is judged pro forma -- planned
 * DCA cash counted as already deployed -- so a glide path in progress does not
 * read as "too much cash".
 */
export const AllocationBars: React.FC<{ classes: ClassRow[]; compact?: boolean }> = ({ classes, compact }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1.25 }} role="list" aria-label="Allocation vs target">
    {classes.map((r) => {
      const pct = Math.max(0, Math.min(100, parseFloat(r.pct) || 0));
      const target = parseFloat(r.target_pct) || 0;
      const label = CLASS_LABEL[r.class] ?? r.class;
      return (
        <Box key={r.class} role="listitem" aria-label={`${label} ${r.pct}% of target ${r.target_pct}%`}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="body2" className="fw-num" sx={{ color: STATUS_COLOR[r.status] }}>
              {r.pct}%{target > 0 ? ` / ${r.target_pct}%` : ''}
              {!compact && r.pro_forma_pct !== r.pct ? ` · pro forma ${r.pro_forma_pct}%` : ''}
              {!compact && <span style={{ color: 'var(--text-secondary)' }}> · {baseMoney(r.value, 0)}</span>}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', height: compact ? 6 : 8, borderRadius: 4, background: 'var(--glass-2, rgba(127,127,127,0.15))' }}>
            <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, borderRadius: 4, background: STATUS_COLOR[r.status], opacity: 0.8 }} />
            {target > 0 && (
              <Box aria-hidden sx={{ position: 'absolute', top: -3, bottom: -3, left: `calc(${Math.min(100, target)}% - 1px)`, width: 2, background: 'var(--text)' }} />
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const TONE = { success: 'var(--inflow-text)', warning: 'var(--warn-text)', info: 'var(--info-text)', default: 'var(--text-secondary)' };

/** The glide path in a line: status chip and expected vs actual. */
export const DCAStatusLine: React.FC<{ dca?: DCAProgress; line?: string }> = ({ dca, line }) => {
  if (!dca || !dca.configured) {
    return <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>No DCA glide path configured.</Typography>;
  }
  const s = dcaStatusLabel(dca.status);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {/* Outlined with the theme's text tokens: filled colour chips are low-contrast in dark mode. */}
      <Chip size="small" variant="outlined" label={`DCA ${s.label.toLowerCase()}`}
        sx={{ color: TONE[s.tone], borderColor: TONE[s.tone], fontWeight: 600 }} />
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
        {line || `${baseMoney(dca.actual, 0)} deployed of ${baseMoney(dca.expected, 0)} expected`}
      </Typography>
    </Box>
  );
};
